import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import { MessageType, UtilsService } from 'src/app/common';
import { SeoComprehensivePdfAdapterService } from './seo-comprehensive-pdf.adapter';
import { SeoComprehensivePdfSourceService } from './seo-comprehensive-pdf-source.service';
import {
  SeoPdfBlock,
  SeoPdfExecutiveCallout,
  SeoPdfMetricCard,
  SeoPdfPage,
  SeoPdfRecommendationItem,
  SeoPdfReportViewModel,
  SeoPdfSummaryTableColumn,
  SeoPdfSummaryTableRow,
} from './seo-comprehensive-pdf.models';

@Injectable({
  providedIn: 'root',
})
export class SeoComprehensivePdfExportService {
  public constructor(
    private readonly sourceService: SeoComprehensivePdfSourceService,
    private readonly adapterService: SeoComprehensivePdfAdapterService,
    private readonly utils: UtilsService
  ) {}

  public async exportPdf(): Promise<boolean> {
    const sourceData = await this.sourceService.buildSourceData();
    if (!sourceData) {
      this.utils.warnMessage(MessageType.TOAST, 'Export unavailable', 'No active project is available for SEO PDF export.');
      return false;
    }

    const report = this.adapterService.buildReport(sourceData);
    if (report.pages.length === 0) {
      this.utils.warnMessage(MessageType.TOAST, 'Export unavailable', 'No report pages could be generated from the current project data.');
      return false;
    }

    this.renderPdf(report);
    this.utils.successMessage(MessageType.TOAST, 'Export complete', 'SEO comprehensive PDF generated successfully.');
    return true;
  }

  private renderPdf(report: SeoPdfReportViewModel): void {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - (margin * 2);
    const palette = {
      ink: [30, 41, 59] as [number, number, number],
      muted: [100, 116, 139] as [number, number, number],
      accent: [14, 116, 144] as [number, number, number],
      accentSoft: [236, 254, 255] as [number, number, number],
      border: [226, 232, 240] as [number, number, number],
      surface: [248, 250, 252] as [number, number, number],
    };

    let y = margin;

    const ensureSpace = (height: number): void => {
      if (y + height <= pageHeight - margin) {
        return;
      }

      doc.addPage();
      y = margin;
    };

    const drawWrappedText = (
      text: string,
      x: number,
      top: number,
      width: number,
      fontSize: number,
      color: [number, number, number] = palette.ink,
      fontStyle: 'normal' | 'bold' = 'normal'
    ): number => {
      doc.setFont('helvetica', fontStyle);
      doc.setFontSize(fontSize);
      doc.setTextColor(color[0], color[1], color[2]);
      const lines = doc.splitTextToSize(text, width);
      doc.text(lines, x, top);
      return lines.length * (fontSize + 4);
    };

    const drawSectionDivider = (): void => {
      doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
      doc.setLineWidth(0.8);
      doc.line(margin, y, pageWidth - margin, y);
      y += 16;
    };

    const drawPageTitle = (title: string, insight: string): void => {
      ensureSpace(80);
      drawSectionDivider();
      y += drawWrappedText(title, margin, y, contentWidth, 18, palette.ink, 'bold');
      y += 6;
      doc.setFillColor(palette.accentSoft[0], palette.accentSoft[1], palette.accentSoft[2]);
      doc.roundedRect(margin, y, contentWidth, 34, 10, 10, 'F');
      y += drawWrappedText(insight, margin + 12, y + 20, contentWidth - 24, 10, palette.accent, 'bold');
      y += 18;
    };

    const drawBullets = (items: string[]): void => {
      items.forEach((item: string) => {
        ensureSpace(18);
        doc.setFillColor(palette.accent[0], palette.accent[1], palette.accent[2]);
        doc.circle(margin + 4, y - 3, 2, 'F');
        y += drawWrappedText(item, margin + 14, y, contentWidth - 14, 10, palette.ink, 'normal');
      });
    };

    const drawMetricGrid = (metrics: SeoPdfMetricCard[]): void => {
      const cardWidth = (contentWidth - 12) / 2;
      const cardHeight = 62;

      metrics.forEach((metric: SeoPdfMetricCard, index: number) => {
        if (index % 2 === 0) {
          ensureSpace(cardHeight + 12);
        }

        const x = margin + ((index % 2) * (cardWidth + 12));
        const top = y + (Math.floor(index / 2) * (cardHeight + 12));
        doc.setFillColor(palette.surface[0], palette.surface[1], palette.surface[2]);
        doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
        doc.roundedRect(x, top, cardWidth, cardHeight, 10, 10, 'FD');
        drawWrappedText(metric.label.toUpperCase(), x + 12, top + 16, cardWidth - 24, 8, palette.muted, 'bold');
        drawWrappedText(metric.value, x + 12, top + 36, cardWidth - 24, 18, palette.ink, 'bold');
      });

      y += (Math.ceil(metrics.length / 2) * (cardHeight + 12));
    };

    const drawSummaryTable = (
      columns: SeoPdfSummaryTableColumn[],
      rows: SeoPdfSummaryTableRow[]
    ): void => {
      if (columns.length === 0 || rows.length === 0) {
        return;
      }

      const columnWidth = contentWidth / columns.length;
      const baseRowHeight = 24;
      ensureSpace(baseRowHeight + 12);

      doc.setFillColor(palette.accentSoft[0], palette.accentSoft[1], palette.accentSoft[2]);
      doc.roundedRect(margin, y, contentWidth, baseRowHeight, 8, 8, 'F');

      columns.forEach((column: SeoPdfSummaryTableColumn, index: number) => {
        drawWrappedText(column.label, margin + (index * columnWidth) + 8, y + 16, columnWidth - 12, 8, palette.ink, 'bold');
      });
      y += baseRowHeight;

      rows.forEach((row: SeoPdfSummaryTableRow, rowIndex: number) => {
        const rowLines = columns.map((column: SeoPdfSummaryTableColumn) => {
          const value = String(row[column.key] ?? '-');
          return doc.splitTextToSize(value, columnWidth - 12);
        });
        const maxLineCount = Math.max(...rowLines.map((lines: string[]) => lines.length), 1);
        const rowHeight = Math.max(baseRowHeight, 10 + (maxLineCount * 12));
        ensureSpace(rowHeight + 4);
        if (rowIndex % 2 === 0) {
          doc.setFillColor(palette.surface[0], palette.surface[1], palette.surface[2]);
          doc.roundedRect(margin, y, contentWidth, rowHeight, 0, 0, 'F');
        }

        columns.forEach((column: SeoPdfSummaryTableColumn, columnIndex: number) => {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(palette.ink[0], palette.ink[1], palette.ink[2]);
          doc.text(rowLines[columnIndex], margin + (columnIndex * columnWidth) + 8, y + 14);
        });
        y += rowHeight;
      });
    };

    const drawParagraphBlock = (text: string): void => {
      ensureSpace(48);
      y += drawWrappedText(text, margin, y, contentWidth, 10, palette.ink, 'normal');
      y += 6;
    };

    const drawPageNarrative = (page: SeoPdfPage): void => {
      drawWrappedText('What the data shows', margin, y, contentWidth, 10, palette.muted, 'bold');
      y += 16;
      y += drawWrappedText(page.whatDataShows, margin, y, contentWidth, 10, palette.ink, 'normal');
      y += 8;

      drawWrappedText('What it means', margin, y, contentWidth, 10, palette.muted, 'bold');
      y += 16;
      y += drawWrappedText(page.whatItMeans, margin, y, contentWidth, 10, palette.ink, 'normal');
      y += 10;

      drawWrappedText('Recommended response', margin, y, contentWidth, 10, palette.muted, 'bold');
      y += 16;
      drawBullets(page.recommendedResponse);
      y += 8;
    };

    const drawCalloutCards = (callouts: SeoPdfExecutiveCallout[]): void => {
      callouts.forEach((callout: SeoPdfExecutiveCallout) => {
        const messageLines = doc.splitTextToSize(callout.message, contentWidth - 32);
        const cardHeight = Math.max(76, 42 + (messageLines.length * 14));
        ensureSpace(cardHeight + 10);
        const accentColor = this.getCalloutAccent(callout.tone);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
        doc.roundedRect(margin, y, contentWidth, cardHeight, 12, 12, 'FD');
        doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
        doc.roundedRect(margin, y, 8, cardHeight, 8, 8, 'F');
        const cardTop = y;
        drawWrappedText(callout.label.toUpperCase(), margin + 18, cardTop + 18, contentWidth - 32, 8, palette.muted, 'bold');
        drawWrappedText(callout.message, margin + 18, cardTop + 38, contentWidth - 32, 10, palette.ink, 'bold');
        y = cardTop + cardHeight + 10;
      });
    };

    const drawRoadmapColumns = (items: SeoPdfRecommendationItem[]): void => {
      const doNow = items.slice(0, 3);
      const doNext = items.slice(3, 6);
      const columnGap = 14;
      const columnWidth = (contentWidth - columnGap) / 2;
      const headerHeight = 26;
      const getCardHeight = (item: SeoPdfRecommendationItem): number => {
        const titleLines = doc.splitTextToSize(item.title, columnWidth - 24);
        const metaLines = doc.splitTextToSize(
          `${item.family} | ${item.effort} effort | ${item.expectedImpact}`,
          columnWidth - 24
        );
        const evidenceLines = doc.splitTextToSize(item.evidenceNote, columnWidth - 24);
        return Math.max(88, 18 + (titleLines.length * 12) + (metaLines.length * 10) + (evidenceLines.length * 10) + 18);
      };
      const getColumnHeight = (cards: SeoPdfRecommendationItem[]): number => {
        return headerHeight + 10 + cards.reduce((sum: number, item: SeoPdfRecommendationItem) => sum + getCardHeight(item) + 10, 0);
      };

      const drawColumn = (
        title: string,
        cards: SeoPdfRecommendationItem[],
        x: number,
        top: number
      ): number => {
        let localY = top;
        doc.setFillColor(palette.accentSoft[0], palette.accentSoft[1], palette.accentSoft[2]);
        doc.roundedRect(x, localY, columnWidth, headerHeight, 10, 10, 'F');
        drawWrappedText(title, x + 12, localY + 17, columnWidth - 24, 10, palette.accent, 'bold');
        localY += headerHeight + 10;

        cards.forEach((item: SeoPdfRecommendationItem) => {
          const cardHeight = getCardHeight(item);
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
          doc.roundedRect(x, localY, columnWidth, cardHeight, 12, 12, 'FD');
          let cardY = localY + 18;
          cardY += drawWrappedText(item.title, x + 12, cardY, columnWidth - 24, 10, palette.ink, 'bold');
          cardY += 2;
          cardY += drawWrappedText(
            `${item.family} | ${item.effort} effort | ${item.expectedImpact}`,
            x + 12,
            cardY,
            columnWidth - 24,
            8.5,
            palette.muted,
            'normal'
          );
          cardY += 2;
          drawWrappedText(item.evidenceNote, x + 12, cardY, columnWidth - 24, 8.5, palette.ink, 'normal');
          localY += cardHeight + 10;
        });

        return localY;
      };

      const estimatedHeight = Math.max(getColumnHeight(doNow), getColumnHeight(doNext));
      ensureSpace(estimatedHeight);
      const startY = y;
      const nowBottom = drawColumn('Do now', doNow, margin, startY);
      const nextBottom = drawColumn('Do next', doNext, margin + columnWidth + columnGap, startY);
      y = Math.max(nowBottom, nextBottom) + 6;
    };

    const drawBlock = (block: SeoPdfBlock): void => {
      switch (block.type) {
        case 'kpi-grid':
          drawMetricGrid(block.payload as SeoPdfMetricCard[]);
          break;
        case 'summary-table': {
          const payload = block.payload as {
            title?: string;
            columns?: SeoPdfSummaryTableColumn[];
            rows?: SeoPdfSummaryTableRow[];
          };
          if (payload.title) {
            ensureSpace(18);
            y += drawWrappedText(payload.title, margin, y, contentWidth, 11, palette.ink, 'bold');
          }
          drawSummaryTable(payload.columns ?? [], payload.rows ?? []);
          y += 6;
          break;
        }
        case 'recommendation-list':
          drawRoadmapColumns(block.payload as SeoPdfRecommendationItem[]);
          break;
        case 'paragraph':
          drawParagraphBlock(block.payload as string);
          break;
        case 'evidence-callout': {
          const payload = block.payload as { title?: string; notes?: string[] };
          if (!payload.notes?.length) {
            break;
          }

          const estimatedHeight = 28 + payload.notes.reduce((sum: number, note: string) => {
            const lines = doc.splitTextToSize(note, contentWidth - 36);
            return sum + Math.max(20, lines.length * 13);
          }, 0);
          ensureSpace(estimatedHeight);
          doc.setFillColor(palette.surface[0], palette.surface[1], palette.surface[2]);
          doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
          doc.roundedRect(margin, y, contentWidth, estimatedHeight, 12, 12, 'FD');
          const top = y;
          let innerY = top + 18;
          if (payload.title) {
            innerY += drawWrappedText(payload.title, margin + 12, innerY, contentWidth - 24, 10, palette.accent, 'bold');
            innerY += 2;
          }
          payload.notes.forEach((note: string) => {
            doc.setFillColor(palette.accent[0], palette.accent[1], palette.accent[2]);
            doc.circle(margin + 16, innerY + 3, 2, 'F');
            innerY += drawWrappedText(note, margin + 24, innerY + 6, contentWidth - 36, 9, palette.ink, 'normal') + 2;
          });
          y = top + estimatedHeight + 10;
          break;
        }
        case 'status-summary': {
          const payload = block.payload as {
            title?: string;
            columns?: SeoPdfSummaryTableColumn[];
            rows?: SeoPdfSummaryTableRow[];
            notes?: string[];
          };
          if (payload.title) {
            ensureSpace(18);
            y += drawWrappedText(payload.title, margin, y, contentWidth, 11, palette.ink, 'bold');
          }
          drawSummaryTable(
            payload.columns ?? [
              { key: 'theme', label: 'Theme' },
              { key: 'status', label: 'Status' },
              { key: 'count', label: 'Count' },
              { key: 'implication', label: 'Business implication' },
            ],
            payload.rows ?? []
          );
          if (payload.notes?.length) {
            drawBullets(payload.notes);
          }
          y += 6;
          break;
        }
        default:
          break;
      }
    };

    this.drawCover(doc, report, pageWidth, margin, contentWidth, palette, drawWrappedText);
    doc.addPage();
    y = margin;
    y = this.drawExecutiveSummaryPage(doc, report, pageWidth, margin, contentWidth, palette, drawWrappedText);
    report.pages.forEach((page: SeoPdfPage) => {
      doc.addPage();
      y = margin;
      drawPageTitle(page.title, page.insight);
      page.blocks.forEach((block: SeoPdfBlock) => drawBlock(block));
      drawPageNarrative(page);
    });

    this.addFooters(doc, report.project.projectName, palette, margin, pageHeight, pageWidth);
    const fileName = `seo-comprehensive-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
  }

  private drawExecutiveSummaryPage(
    doc: jsPDF,
    report: SeoPdfReportViewModel,
    pageWidth: number,
    margin: number,
    contentWidth: number,
    palette: {
      ink: [number, number, number];
      muted: [number, number, number];
      accent: [number, number, number];
      accentSoft: [number, number, number];
      border: [number, number, number];
      surface: [number, number, number];
    },
    drawWrappedText: (
      text: string,
      x: number,
      top: number,
      width: number,
      fontSize: number,
      color?: [number, number, number],
      fontStyle?: 'normal' | 'bold'
    ) => number
  ): number {
    let currentY = margin;
    const cardGap = 12;
    const metricCardWidth = (contentWidth - cardGap) / 2;
    const metricCardHeight = 66;
    const drawMetricGrid = (metrics: SeoPdfMetricCard[]): void => {
      metrics.forEach((metric: SeoPdfMetricCard, index: number) => {
        const x = margin + ((index % 2) * (metricCardWidth + cardGap));
        const top = currentY + (Math.floor(index / 2) * (metricCardHeight + cardGap));
        doc.setFillColor(palette.surface[0], palette.surface[1], palette.surface[2]);
        doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
        doc.roundedRect(x, top, metricCardWidth, metricCardHeight, 10, 10, 'FD');
        drawWrappedText(metric.label.toUpperCase(), x + 12, top + 16, metricCardWidth - 24, 8, palette.muted, 'bold');
        drawWrappedText(metric.value, x + 12, top + 38, metricCardWidth - 24, 18, palette.ink, 'bold');
      });

      currentY += Math.ceil(metrics.length / 2) * (metricCardHeight + cardGap);
    };

    const drawCalloutCards = (callouts: SeoPdfExecutiveCallout[]): void => {
      callouts.forEach((callout: SeoPdfExecutiveCallout) => {
        const messageLines = doc.splitTextToSize(callout.message, contentWidth - 32);
        const calloutHeight = Math.max(76, 42 + (messageLines.length * 14));
        const accentColor = this.getCalloutAccent(callout.tone);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
        doc.roundedRect(margin, currentY, contentWidth, calloutHeight, 12, 12, 'FD');
        doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
        doc.roundedRect(margin, currentY, 8, calloutHeight, 8, 8, 'F');
        const calloutTop = currentY;
        drawWrappedText(callout.label.toUpperCase(), margin + 18, calloutTop + 18, contentWidth - 32, 8, palette.muted, 'bold');
        drawWrappedText(callout.message, margin + 18, calloutTop + 38, contentWidth - 32, 10, palette.ink, 'bold');
        currentY = calloutTop + calloutHeight + 10;
      });
    };

    const drawBullets = (items: string[]): void => {
      items.forEach((item: string) => {
        doc.setFillColor(palette.accent[0], palette.accent[1], palette.accent[2]);
        doc.circle(margin + 4, currentY - 3, 2, 'F');
        currentY += drawWrappedText(item, margin + 14, currentY, contentWidth - 14, 10, palette.ink, 'normal');
      });
    };

    doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 20;
    currentY += drawWrappedText('Executive Summary', margin, currentY, contentWidth, 20, palette.ink, 'bold');
    currentY += 8;
    doc.setFillColor(palette.accentSoft[0], palette.accentSoft[1], palette.accentSoft[2]);
    doc.roundedRect(margin, currentY, contentWidth, 38, 12, 12, 'F');
    currentY += drawWrappedText(report.healthSummary.overallStatus, margin + 12, currentY + 22, contentWidth - 24, 11, palette.accent, 'bold');
    currentY += 26;

    drawMetricGrid(report.executiveSummary.metrics.slice(0, 4));
    currentY += 6;
    currentY += drawWrappedText(report.executiveSummary.summaryParagraph, margin, currentY, contentWidth, 10, palette.ink, 'normal');
    currentY += 14;

    drawCalloutCards(report.executiveSummary.callouts);
    currentY += 6;

    currentY += drawWrappedText('What to do now', margin, currentY, contentWidth, 11, palette.ink, 'bold');
    currentY += 14;
    drawBullets(report.executiveSummary.actions);

    if (report.dataAvailability.notes.length > 0) {
      currentY += 10;
      currentY += drawWrappedText('Data notes', margin, currentY, contentWidth, 10, palette.muted, 'bold');
      currentY += 12;
      drawBullets(report.dataAvailability.notes.slice(0, 3));
    }

    return currentY;
  }

  private drawCover(
    doc: jsPDF,
    report: SeoPdfReportViewModel,
    pageWidth: number,
    margin: number,
    contentWidth: number,
    palette: {
      ink: [number, number, number];
      muted: [number, number, number];
      accent: [number, number, number];
      accentSoft: [number, number, number];
      border: [number, number, number];
      surface: [number, number, number];
    },
    drawWrappedText: (
      text: string,
      x: number,
      top: number,
      width: number,
      fontSize: number,
      color?: [number, number, number],
      fontStyle?: 'normal' | 'bold'
    ) => number
  ): void {
    let y = margin + 30;

    doc.setFillColor(palette.accentSoft[0], palette.accentSoft[1], palette.accentSoft[2]);
    doc.rect(0, 0, pageWidth, 170, 'F');

    y += drawWrappedText(report.project.domain || report.project.projectName, margin, y, contentWidth, 12, palette.accent, 'bold');
    y += 18;
    y += drawWrappedText('SEO Performance and Opportunity Report', margin, y, contentWidth, 24, palette.ink, 'bold');
    y += 18;
    y += drawWrappedText(
      'A summary of visibility, authority, technical health, and next-step SEO priorities.',
      margin,
      y,
      contentWidth - 80,
      11,
      palette.muted,
      'normal'
    );
    y += 40;

    drawWrappedText(`Generated: ${new Date(report.project.generatedAt).toLocaleString()}`, margin, y, contentWidth, 10, palette.muted, 'normal');
    y += 24;

    drawWrappedText('This report covers:', margin, y, contentWidth, 11, palette.ink, 'bold');
    y += 18;
    [
      'Current SEO and traffic posture',
      'Technical risk and ranking constraints',
      'Highest-priority next actions',
    ].forEach((line: string) => {
      doc.setFillColor(palette.accent[0], palette.accent[1], palette.accent[2]);
      doc.circle(margin + 4, y - 4, 2, 'F');
      y += drawWrappedText(line, margin + 14, y, contentWidth - 14, 11, palette.ink, 'normal');
    });
  }

  private addFooters(
    doc: jsPDF,
    projectName: string,
    palette: {
      ink: [number, number, number];
      muted: [number, number, number];
      accent: [number, number, number];
      accentSoft: [number, number, number];
      border: [number, number, number];
      surface: [number, number, number];
    },
    margin: number,
    pageHeight: number,
    pageWidth: number
  ): void {
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
      doc.line(margin, pageHeight - 24, pageWidth - margin, pageHeight - 24);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(palette.muted[0], palette.muted[1], palette.muted[2]);
      doc.text(projectName, margin, pageHeight - 10);
      doc.text(`Page ${page} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }
  }

  private getCalloutAccent(tone: SeoPdfExecutiveCallout['tone']): [number, number, number] {
    switch (tone) {
      case 'positive':
        return [22, 163, 74];
      case 'warning':
        return [217, 119, 6];
      case 'negative':
        return [220, 38, 38];
      case 'info':
        return [14, 116, 144];
      default:
        return [100, 116, 139];
    }
  }
}
